import { useNavigate, useParams } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { toDateTimeLocalValue } from "@/shared/lib/datetime-local-form";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useRequestQuery, useUpdateRequest } from "../api/queries";
import { RequestForm } from "../ui/RequestForm";

export function RequestEditPage() {
  const { id } = useParams({ from: "/app/performance/requests/$id/edit" });
  const nav = useNavigate({ from: "/app/performance/requests/$id/edit" });
  const { data: request, isLoading, isError, error, refetch } =
    useRequestQuery(id);
  const updateRequest = useUpdateRequest(id);

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ຄິວ", to: "/app/performance/requests" },
              { label: "..." },
            ]}
          />
        </Header>
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        </Main>
      </>
    );
  }

  if (isError || !request) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ຄິວ", to: "/app/performance/requests" },
              { label: "ແກ້ໄຂ" },
            ]}
          />
        </Header>
        <Main>
          <p className="text-destructive text-sm">
            ໂຫຼດຄຳຂໍບໍ່ສຳເລັດ
            {error instanceof Error && error.message
              ? ` — ${error.message}`
              : ""}
          </p>
          <button
            type="button"
            className="mt-3 text-primary text-sm underline"
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄິວ", to: "/app/performance/requests" },
            {
              label: request.title,
              to: "/app/performance/requests/$id",
              params: { id },
            },
            { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6">
          <h2 className="font-bold text-2xl tracking-tight">ແກ້ໄຂຄຳຂໍ</h2>
          <p className="text-muted-foreground">{request.title}</p>
        </div>

        <div className="rounded-xl border bg-card px-6 pt-6">
          <RequestForm
            excludeRequestId={id}
            excludeEventId={request.convertedEventId ?? undefined}
            initialValues={{
              title: request.title,
              customerName: request.customerName,
              contactName: request.contactName,
              contactPhone: request.contactPhone,
              contactEmail: request.contactEmail,
              contactAddress: request.contactAddress,
              channel: request.channel,
              status: request.status,
              categoryId: request.categoryId,
              dateMode: request.dateMode,
              requestedEventDate:
                toDateTimeLocalValue(request.requestedEventDate) || null,
              showDates: (request.showDates ?? []).map((row) =>
                toDateTimeLocalValue(row.showDate),
              ),
              priorityOrder: request.priorityOrder,
              assignedTo: request.assignedTo,
              notes: request.notes,
            }}
            onSubmit={async (vals) => {
              try {
                await updateRequest.mutateAsync(vals);
                nav({
                  to: "/app/performance/requests/$id",
                  params: { id },
                });
              } catch {
                // toast handled by mutation onError
              }
            }}
            onCancel={() =>
              nav({
                to: "/app/performance/requests/$id",
                params: { id },
              })
            }
            submitting={updateRequest.isPending}
          />
        </div>
      </Main>
    </>
  );
}
